import React from "react";
import { IconCheckRadio, IconWarning } from "@geotab/zenith";
import LoginTranslations from "../../../helpers/translations";
import type { IPasswordRule } from "../../../helpers/passwordRules";
import type { TTranslate } from "../../../helpers/renderUntilAnswered";

interface IPasswordPolicyListProps {
    rules: IPasswordRule[];
    password: string;
    isAccepted: boolean;
    isTyping: boolean;
    isUpdatedPageEnabled: boolean;
    textClassName: string;
    translate: TTranslate;
}

interface IPasswordRuleEntryProps {
    rule: IPasswordRule;
    isMet: boolean | undefined;
    translate: TTranslate;
}

const getMarkClassName = (isMet: boolean | undefined) => {
    if (isMet === undefined) {
        return "";
    }
    return isMet ? " update-password__satisfied" : " update-password__violation";
};

const getRuleSentence = (rule: IPasswordRule, translate: TTranslate) => translate(rule.sentence).replace("{0}", String(rule.count));

const MarkedRule: React.FC<IPasswordRuleEntryProps> = ({ rule, isMet, translate }: IPasswordRuleEntryProps) => {
    const Icon = isMet ? IconCheckRadio : IconWarning;
    return (
        <li id={rule.id} className={`update-password__rule${ getMarkClassName(isMet) }`}>
            {/* a new key on each change has the whole rule announced, not only its state */}
            <span key={String(isMet)} className="update-password__rule-content">
                {isMet === undefined ? <span className="update-password__rule-spacer" /> : <Icon className="update-password__rule-icon" size="big" />}
                <span>{getRuleSentence(rule, translate)}</span>
                {isMet !== undefined && (
                    <span className="zen-visually-hidden">{translate(isMet ? LoginTranslations.RequirementMet : LoginTranslations.RequirementNotMet)}</span>
                )}
            </span>
        </li>
    );
};

const PasswordPolicyList: React.FC<IPasswordPolicyListProps> = ({ rules, password, isAccepted, isTyping, isUpdatedPageEnabled, textClassName, translate }: IPasswordPolicyListProps) => {
    // the updated page leaves a cleared password unmarked
    const isMarked = isUpdatedPageEnabled && password.length > 0;
    const isFlagged = (isMet: boolean | undefined) => (isUpdatedPageEnabled ? isMarked : isTyping) && isMet === false;
    const getLabelClassName = () => {
        if (isFlagged(isAccepted)) {
            return "update-password__policy-label update-password__violation";
        }
        return isMarked ? "update-password__policy-label update-password__satisfied" : "update-password__policy-label";
    };

    return (
        <div className={`update-password__policy ${ textClassName }`}>
            <label htmlFor="update-password-policy" className={getLabelClassName()}>
                {translate(LoginTranslations.PasswordMust)}:
            </label>
            <ul
                id="update-password-policy"
                className={isMarked ? "update-password__policy-list update-password__policy-list--marked" : "update-password__policy-list"}
                aria-live={isUpdatedPageEnabled ? "polite" : undefined}
            >
                {rules.map(rule => isMarked
                    ? <MarkedRule key={rule.id} rule={rule} isMet={rule.isMet(password)} translate={translate} />
                    : (
                        <li key={rule.id} id={rule.id} className={isFlagged(rule.isMet(password)) ? "update-password__violation" : ""}>
                            {getRuleSentence(rule, translate)}
                        </li>
                    ))}
            </ul>
        </div>
    );
};

export default PasswordPolicyList;
